var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useRef } from 'react';
import { Dropdown } from '@widget/popups/Dropdown';
import { getFontAwesomeIcon } from '@utils';
import { hasValue } from '@utils';
import { UXPContext } from '@core/context/Context';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import ButtonListContext from '@widget/buttons/ButtonListContext';
/**
 * @export
 * A component that renders a list of actions with support for nested dropdowns and selected state.
 *
 * @example
 * ```
 * // Basic usage
 * <ActionsListComponent
 *   actions={[
 *     { label: 'Edit', icon: 'edit', onClick: async (item) => console.log('Edit', item) },
 *     { label: 'Delete', icon: 'trash', onClick: async (item) => console.log('Delete', item) }
 *   ]}
 *   item={{ id: '1', name: 'Item' }}
 * />
 * ```
 *
 * @example
 * ```
 * // With selected state (e.g., theme selector)
 * <ActionsListComponent
 *   actions={[
 *     { label: 'Light', value: 'light', onClick: () => setTheme('light') },
 *     { label: 'Dark', value: 'dark', onClick: () => setTheme('dark') }
 *   ]}
 *   selectedValue="light"
 * />
 * ```
 *
 * @example
 * ```
 * // With nested dropdowns
 * <ActionsListComponent
 *   actions={[
 *     { label: 'Edit', icon: 'edit', onClick: async (item) => console.log('Edit', item) },
 *     {
 *       label: 'More', icon: 'ellipsis-h',
 *       children: [
 *         { label: 'Share', icon: 'share', onClick: async (item) => console.log('Share', item) },
 *         { label: 'Archive', icon: 'archive', onClick: async (item) => console.log('Archive', item) }
 *       ]
 *     }
 *   ]}
 *   item={{ id: '1', name: 'Item' }}
 *   className="custom-actions-list"
 * />
 * ```
 *
 * @partOf TableComponent
 */
const ActionsListComponent = ({ actions, item, selectedValue, className, parentDropdownRef }) => {
    const context = useContext(UXPContext);
    // In a list of actions (phones: More sheet) an action also closes the list
    const listMode = useContext(ButtonListContext);
    const renderAction = (action, index) => {
        if (!("children" in action)) {
            const isSelected = hasValue(selectedValue) && hasValue(action.value) && action.value === selectedValue;
            const iconRight = action.iconPosition === 'right';
            const iconEl = hasValue(action.icon) && (_jsx("div", { className: "uxpcore_actions_list__action-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(action.icon) }) }));
            const textEl = (_jsx("div", { className: "uxpcore_actions_list__action-text", children: typeof action.label === "string" ? context.$L(action.label) : action.label }));
            return (_jsx("div", { className: `uxpcore_actions_list__action${isSelected ? ' uxpcore_actions_list__action--selected' : ''}${iconRight ? ' uxpcore_actions_list__action--icon-right' : ''}`, onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                    var _a, _b;
                    yield action.onClick(item);
                    // Close parent dropdown after action executes
                    (_a = parentDropdownRef === null || parentDropdownRef === void 0 ? void 0 : parentDropdownRef.current) === null || _a === void 0 ? void 0 : _a.close();
                    (_b = listMode === null || listMode === void 0 ? void 0 : listMode.onAction) === null || _b === void 0 ? void 0 : _b.call(listMode);
                }), children: iconRight ? _jsxs(_Fragment, { children: [textEl, iconEl] }) : _jsxs(_Fragment, { children: [iconEl, textEl] }) }, index));
        }
        // For nested dropdowns, create a new ref and pass it down
        const nestedDropdownRef = (action === null || action === void 0 ? void 0 : action.ref) || useRef(null);
        return (_jsx(Dropdown, { trigger: _jsxs("div", { className: "uxpcore_actions_list__action", children: [hasValue(action.icon) && (_jsx("div", { className: "uxpcore_actions_list__action-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(action.icon) }) })), _jsx("div", { className: "uxpcore_actions_list__action-text", children: typeof action.label === "string" ? context.$L(action.label) : action.label })] }), content: _jsx(ActionsListComponent, { actions: action.children, item: item, selectedValue: selectedValue, parentDropdownRef: nestedDropdownRef }), position: 'right-top', ref: nestedDropdownRef }, index));
    };
    const buildActionList = () => {
        const result = typeof actions === 'function' ? actions(item) : actions;
        if (Array.isArray(result)) {
            // Filter actions based on visibility function
            const visibleActions = result.filter(action => {
                // If visible function not provided, always show (default true)
                if (!action.visible)
                    return true;
                // If visible function provided, call it with item data
                return action.visible(item);
            });
            return visibleActions.map((action, index) => (_jsx(React.Fragment, { children: renderAction(action, index) }, index)));
        }
        return result;
    };
    return (_jsx("div", { className: `uxpcore_actions_list ${className || ''}`, children: buildActionList() }));
};
export default ActionsListComponent;
